const colors = {
  orange: {
    light: '#faaf2c',
    default: '#f7931e',
    dark: '#f67d00',
    snowed: '#f4c286',
  },
  bleen: {
    dark: '#1c6f6a',
    default: '#238B85',
    light: '#4ea29d',
  },
  green: {
    light: '#9bde72',
    default: '#76c22c',
    dark: '#319626',
    snowed: '#DAF0CB',
  },
  sepia: {
    light: '#a5916e',
    default: '#8a755d',
    dark: '#75675c',
  },
  cyan: {
    light: '#2EE8EA',
    default: '#1DD7D9',
  },
  yellow: {
    light: '#e2d54e',
    default: '#dbcb22',
    dark: '#afa21b',
  },
  grey: {
    darker: '#34312c',
    dark: '#3b342b',
    default: '#4b453b',
    light: '#918e8a',
  },
  brown: {
    default: '#4c453b',
  },
  smoke: {
    darker: '#131313',
    dark: '#cbc6bf',
    default: '#dedbd7',
    light: '#e6e4e1',
  },
  snow: {
    dark: '#ebe9e6',
    default: '#f2f0ed',
    light: '#faf9f7',
  },
  red: {
    dark: '#e54141',
    default: '#ff4949',
    light: '#ff5b5b',
  },
  white: {
    default: '#ffffff',
  },
} as const;

const colorsUi = {
  primary: '#f7931e',
  success: '#13ce66',
  error: '#ff4949',
  warning: '#ffc82c',
  disabled: {
    border: colors.smoke.dark,
    text: colors.smoke.default,
    background: colors.snow.default,
  },
} as const;

type Palette = typeof colors;
type ColorName = keyof Palette;
type ColorShade<N extends ColorName> = keyof Palette[N];

const palette: { [N in ColorName]: Record<ColorShade<N>, string> } = colors;

type UiColors = typeof colorsUi;
type UiColorName = keyof UiColors;
type UiDisabledPart = keyof UiColors['disabled'];

export const hexToRgb = (hex: string, opacity = 1) => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m: string, r: string, g: string, b: string) => r + r + g + g + b + b);

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `rgba(${parseInt(result[1]!, 16)},${parseInt(result[2]!, 16)},${parseInt(result[3]!, 16)},${opacity})`
    : null;
};

export const color = <N extends ColorName>(name: N, shade: ColorShade<N> = 'default'): string => {
  const shades = palette[name];
  return shades && shades[shade];
};

export function ui(name: 'disabled', part: UiDisabledPart): string;
export function ui(name: Exclude<UiColorName, 'disabled'>): string;
export function ui(name: UiColorName, part?: UiDisabledPart): string | UiColors['disabled'] | null {
  return name ? (part ? colorsUi['disabled'][part] : colorsUi[name]) : null;
}
